import { createAsyncThunk } from '@reduxjs/toolkit';
import { Services } from 'services';
import LearningServices from 'services/LearningServices';
import ContactsService from 'services/ContactsService';
import UsersServices from 'services/UsersServices';
import { downloadFileFromBlob, uploadImagesWithPresignedUrls, uploadFilesWithPresignedUrls, getImageFormat } from 'utils/files';
import { transformErrorResponse } from 'utils';
import { prettifyErrorResponse } from 'utils/generalUtils';
import { actionMapForOtherLoaders, actionMapForDataLoaders } from 'constants/index';
import {
  ICreateDrugTestingScheduleInput,
  IDrugTestingScheduleRequestBody,
  ICreateThirdPartyDrugTestResultInput,
  ICreateThirdPartyDrugTestResultBody,
  IUpdateThirdPartyDrugTestResultBody,
  IThirdPartyDocumentPresignedUrl,
} from 'models/drugTesting';

// Shapes the flat schedule input into the request body the API expects.
// Worker self: omits workerId + invoiceToCompanyId and nests panelIds inside the command.
// Admin/by-id: includes workerId + invoiceToCompanyId, panelIds is a sibling of the command.
const buildDrugTestingScheduleBody = (
  data: ICreateDrugTestingScheduleInput,
  isWorker: boolean,
): IDrugTestingScheduleRequestBody => {
  const { panelIds, workerId, invoiceToCompanyId, ...sharedCommand } = data;
  if (isWorker) {
    return {
      workerDrugTestingScheduleCommand: { ...sharedCommand, panelIds },
    };
  }
  return {
    workerDrugTestingScheduleCommand: { workerId, invoiceToCompanyId, ...sharedCommand },
    panelIds,
  };
};

const fetchConsentFormInformation = createAsyncThunk(
  actionMapForOtherLoaders.fetchConsentFormInformation,
  async (
    query: {
      workerId: string;
      projectId: string;
      companyId?: string;
      languageCode?: string;
    },
    thunkApi,
  ) => {
    return Services.getConsentForm(query.workerId, query.projectId, query.companyId, query.languageCode)
      .then(response => {
        return response.data || {};
      })
      .catch(error => {
        return thunkApi.rejectWithValue(transformErrorResponse(error));
      });
  },
);

const downloadConsentFormPdf = createAsyncThunk(
  actionMapForOtherLoaders.downloadConsentFormPdf,
  async (
    query: {
      workerId: string;
      projectId: string;
      companyId?: string;
      languageCode?: string;
      fileName?: string;
    },
    thunkApi,
  ) => {
    return Services.fetchConsentFormPdf(query.workerId, query.projectId, query.companyId, query.languageCode)
      .then(response => {
        downloadFileFromBlob(response.data, query.fileName || 'consent-form.pdf');
        return true;
      })
      .catch(error => {
        return thunkApi.rejectWithValue(transformErrorResponse(error));
      });
  },
);

const fetchMyCertifications = createAsyncThunk(
  actionMapForOtherLoaders.fetchMyCertifications,
  async (
    query: {
      workerId: string;
      pageNumber: number;
      pageSize: number;
      filterCertificateStatus: number;
      sortCriteria?: string;
      languageCode?: string;
      projectIds?: string[];
      filterCertificateRelatedTo?: number;
    },
    thunkApi,
  ) => {
    const state = thunkApi.getState() as any;
    const isWorker = state.user.isWorker || false;

    return Services.getMyCertifications(
      query.workerId,
      {
        pageNumber: query.pageNumber,
        pageSize: query.pageSize,
        filterCertificateStatus: query.filterCertificateStatus,
        sortCriteria: query.sortCriteria,
        projectIds: query.projectIds,
        filterCertificateRelatedTo: query.filterCertificateRelatedTo,
      },
      query.languageCode || 'en',
      isWorker,
    )
      .then(response => {
        return response.data;
      })
      .catch(error => {
        return thunkApi.rejectWithValue(transformErrorResponse(error));
      });
  },
);

const fetchRequiredCertifications = createAsyncThunk(
  actionMapForOtherLoaders.fetchRequiredCertifications,
  async (
    query: {
      workerId: string;
      pageNumber: number;
      pageSize: number;
      languageCode?: string;
      projectIds?: string[];
      sortCriteria?: string;
    },
    thunkApi,
  ) => {
    const state = thunkApi.getState() as any;
    const isWorker = state.user.isWorker || false;

    return Services.getRequiredCertifications(
      query.workerId,
      {
        pageNumber: query.pageNumber,
        pageSize: query.pageSize,
        projectIds: query.projectIds,
        sortCriteria: query.sortCriteria,
      },
      query.languageCode || 'en',
      isWorker,
    )
      .then(response => {
        return response.data;
      })
      .catch(error => {
        return thunkApi.rejectWithValue(transformErrorResponse(error));
      });
  },
);

const addMyCertification = createAsyncThunk(
  actionMapForOtherLoaders.addMyCertification,
  async (
    query: {
      workerId: string;
      certificationData: {
        certificationId: string;
        idNumber?: string;
        description?: string;
        completionDate?: string;
        expirationDate?: string;
      };
      images?: Array<{ tempId?: string; image: string; fileName?: string }>;
      languageCode?: string;
      // Refresh parameters
      pageNumber: number;
      pageSize: number;
      filterCertificateStatus: number;
      sortCriteria?: string;
      projectIds?: string[];
    },
    thunkApi,
  ) => {
    const state = thunkApi.getState() as any;
    const isWorker = state.user.isWorker || false;

    return Services.addCertification(query.workerId, query.certificationData, query.languageCode || 'en', isWorker)
      .then(async addCertificationResponse => {
        const createdCertificationId = addCertificationResponse.data?.id;

        // Upload images if provided
        if (query.images && query.images.length > 0 && createdCertificationId) {
          try {
            await uploadImagesWithPresignedUrls({
              apiFunction: Services.uploadCertificate,
              apiFunctionArgs: [
                query.workerId,
                createdCertificationId,
                {
                  fileNames: query.images.map(img => {
                    if (img.fileName) return img.fileName;
                    const imageFormat = getImageFormat(img.image);
                    return `${img.tempId}.${imageFormat}`;
                  }),
                },
                query.languageCode || 'en',
              ],
              images: query.images,
              languageCode: query.languageCode || 'en',
            });
          } catch (imageUploadError) {
            // Continue even if image upload fails
          }
        }

        // Refresh using v2 API with pagination/sorting
        // Use the required certifications endpoint when filterCertificateStatus is 2 (REQUIRED)
        let refreshResponse;
        if (query.filterCertificateStatus === 2) {
          refreshResponse = await Services.getRequiredCertifications(
            query.workerId,
            {
              pageNumber: query.pageNumber,
              pageSize: query.pageSize,
              projectIds: query.projectIds,
            },
            query.languageCode || 'en',
            isWorker,
          );
        } else {
          refreshResponse = await Services.getMyCertifications(
            query.workerId,
            {
              pageNumber: query.pageNumber,
              pageSize: query.pageSize,
              filterCertificateStatus: query.filterCertificateStatus,
              sortCriteria: query.sortCriteria,
              projectIds: query.projectIds,
            },
            query.languageCode || 'en',
            isWorker,
          );
        }

        return {
          data: refreshResponse.data,
          filterCertificateStatus: query.filterCertificateStatus,
        };
      })
      .catch(error => {
        return thunkApi.rejectWithValue(transformErrorResponse(error));
      });
  },
);

const updateMyCertification = createAsyncThunk(
  actionMapForOtherLoaders.updateMyCertification,
  async (
    query: {
      workerId: string;
      certificationId: string;
      certificationData: {
        certificationId: string;
        idNumber?: string;
        description?: string;
        completionDate?: string;
        expirationDate?: string;
      };
      images?: Array<{ tempId?: string; image: string; fileName?: string }>;
      deletedFileIds?: string[];
      languageCode?: string;
      // Refresh parameters
      pageNumber: number;
      pageSize: number;
      filterCertificateStatus: number;
      sortCriteria?: string;
      projectIds?: string[];
    },
    thunkApi,
  ) => {
    return Services.updateCertification(
      query.workerId,
      query.certificationId,
      query.certificationData,
      query.languageCode || 'en',
    )
      .then(async updateResponse => {
        // Delete files if needed
        if (query.deletedFileIds && query.deletedFileIds.length > 0) {
          try {
            await Services.deleteCertificationFiles(
              query.workerId,
              query.certificationId,
              {
                workerId: query.workerId,
                workerCertificationId: query.certificationId,
                fileIds: query.deletedFileIds,
              },
              query.languageCode || 'en',
            );
          } catch (deleteError) {
            // Continue even if delete fails
          }
        }

        // Upload new images if provided
        if (query.images && query.images.length > 0) {
          try {
            await uploadImagesWithPresignedUrls({
              apiFunction: Services.uploadCertificate,
              apiFunctionArgs: [
                query.workerId,
                query.certificationId,
                {
                  fileNames: query.images.map(img => {
                    if (img.fileName) return img.fileName;
                    const imageFormat = getImageFormat(img.image);
                    return `${img.tempId}.${imageFormat}`;
                  }),
                },
                query.languageCode || 'en',
              ],
              images: query.images,
              languageCode: query.languageCode || 'en',
            });
          } catch (imageUploadError) {
            // Continue even if image upload fails
          }
        }

        // Refresh using v2 API with pagination/sorting
        const state = thunkApi.getState() as any;
        const isWorker = state.user.isWorker || false;

        const refreshResponse = await Services.getMyCertifications(
          query.workerId,
          {
            pageNumber: query.pageNumber,
            pageSize: query.pageSize,
            filterCertificateStatus: query.filterCertificateStatus,
            sortCriteria: query.sortCriteria,
            projectIds: query.projectIds,
          },
          query.languageCode || 'en',
          isWorker,
        );

        return {
          data: refreshResponse.data,
          filterCertificateStatus: query.filterCertificateStatus,
        };
      })
      .catch(error => {
        return thunkApi.rejectWithValue(transformErrorResponse(error));
      });
  },
);

const deleteMyCertification = createAsyncThunk(
  actionMapForOtherLoaders.deleteMyCertification,
  async (
    query: {
      workerId: string;
      certificateId: string;
      languageCode?: string;
      // Refresh parameters
      pageNumber: number;
      pageSize: number;
      filterCertificateStatus: number;
      sortCriteria?: string;
      projectIds?: string[];
    },
    thunkApi,
  ) => {
    return Services.deleteCertification(query.workerId, query.certificateId, query.languageCode)
      .then(async () => {
        // Refresh using v2 API with pagination/sorting
        const state = thunkApi.getState() as any;
        const isWorker = state.user.isWorker || false;

        const refreshResponse = await Services.getMyCertifications(
          query.workerId,
          {
            pageNumber: query.pageNumber,
            pageSize: query.pageSize,
            filterCertificateStatus: query.filterCertificateStatus,
            sortCriteria: query.sortCriteria,
            projectIds: query.projectIds,
          },
          query.languageCode || 'en',
          isWorker,
        );

        return {
          data: refreshResponse.data,
          filterCertificateStatus: query.filterCertificateStatus,
        };
      })
      .catch(error => {
        return thunkApi.rejectWithValue(transformErrorResponse(error));
      });
  },
);

const fetchWorkerDrugTestingSchedules = createAsyncThunk(
  actionMapForOtherLoaders.fetchWorkerDrugTestingSchedules,
  async (
    query: {
      workerId: string;
      pageNumber: number;
      pageSize: number;
      projectId?: string;
      languageCode?: string;
    },
    thunkApi,
  ) => {
    const state = thunkApi.getState() as any;
    const isWorker = state.user.isWorker || false;

    return Services.getWorkerDrugTestingSchedules(
      query.workerId,
      {
        pageNumber: query.pageNumber,
        pageSize: query.pageSize,
        projectId: query.projectId,
      },
      query.languageCode || 'en',
      isWorker,
    )
      .then(response => {
        return response.data;
      })
      .catch(error => {
        return thunkApi.rejectWithValue(transformErrorResponse(error));
      });
  },
);

const createWorkerDrugTestingSchedule = createAsyncThunk(
  actionMapForOtherLoaders.createWorkerDrugTestingSchedule,
  async (input: ICreateDrugTestingScheduleInput, thunkApi) => {
    const state = thunkApi.getState() as any;
    const languageCode = state.app?.languageCode || 'en';
    const isWorker = state.user.isWorker || false;
    const body = buildDrugTestingScheduleBody(input, isWorker);

    return Services.createDrugTestingSchedule(body, languageCode, isWorker)
      .then(response => {
        return response.data;
      })
      .catch(error => {
        return thunkApi.rejectWithValue(transformErrorResponse(error));
      });
  },
);

const updateWorkerDrugTestingSchedule = createAsyncThunk(
  actionMapForOtherLoaders.updateWorkerDrugTestingSchedule,
  async (query: { scheduleId: string; input: ICreateDrugTestingScheduleInput }, thunkApi) => {
    const state = thunkApi.getState() as any;
    const languageCode = state.app?.languageCode || 'en';
    const isWorker = state.user.isWorker || false;
    const body = buildDrugTestingScheduleBody(query.input, isWorker);

    return Services.updateDrugTestingSchedule(query.scheduleId, body, languageCode, isWorker)
      .then(response => {
        return response.data;
      })
      .catch(error => {
        return thunkApi.rejectWithValue(transformErrorResponse(error));
      });
  },
);

const deleteWorkerDrugTestingSchedule = createAsyncThunk(
  actionMapForOtherLoaders.deleteWorkerDrugTestingSchedule,
  async (query: { scheduleId: string }, thunkApi) => {
    const state = thunkApi.getState() as any;
    const languageCode = state.app?.languageCode || 'en';
    const isWorker = state.user.isWorker || false;

    return Services.deleteDrugTestingSchedule(query.scheduleId, languageCode, isWorker)
      .then(() => query.scheduleId)
      .catch(error => thunkApi.rejectWithValue(transformErrorResponse(error)));
  },
);

const fetchWorkerThirdPartyDrugTestResults = createAsyncThunk(
  actionMapForOtherLoaders.fetchWorkerThirdPartyDrugTestResults,
  async (
    query: {
      workerId: string;
      pageNumber: number;
      pageSize: number;
      languageCode?: string;
    },
    thunkApi,
  ) => {
    const state = thunkApi.getState() as any;
    const isWorker = state.user.isWorker || false;

    return Services.getWorkerThirdPartyDrugTestResults(
      query.workerId,
      { pageNumber: query.pageNumber, pageSize: query.pageSize },
      query.languageCode || 'en',
      isWorker,
    )
      .then(response => {
        return response.data;
      })
      .catch(error => {
        return thunkApi.rejectWithValue(transformErrorResponse(error));
      });
  },
);

const createWorkerThirdPartyDrugTestResult = createAsyncThunk(
  actionMapForOtherLoaders.createWorkerThirdPartyDrugTestResult,
  async (input: ICreateThirdPartyDrugTestResultInput, thunkApi) => {
    const state = thunkApi.getState() as any;
    const languageCode = state.app?.languageCode || 'en';
    const isWorker = state.user.isWorker || false;
    const workerId = state.onboarding.id || state.user?.workerId;

    const createBody: ICreateThirdPartyDrugTestResultBody = {
      projectId: input.projectId,
      panelId: input.panelId,
      drugTestingTypeId: input.drugTestingTypeId,
      testedAt: input.testedAt,
      clinicName: input.clinicName,
      reason: input.reason,
      result: input.result,
    };

    // Captured once the record is created so a post-create failure (PATCH / file upload)
    // can hand the id back — the UI retries as an edit on this record, never a duplicate.
    let createdResultId: string | undefined;
    try {
      const createResponse = await Services.createThirdPartyDrugTestResult(createBody, languageCode, isWorker, workerId);
      createdResultId = createResponse.data;

      if (input.files.length > 0) {
        // Set-state PATCH: keep existing docs by fileId, mark new uploads with fileId null.
        const patchBody = { documents: input.files.map(f => ({ fileId: f.fileId, fileName: f.name })) };
        const patchResponse = await Services.patchThirdPartyDrugTestResultDocuments(
          createdResultId,
          patchBody,
          languageCode,
          isWorker,
          workerId,
        );
        const presignedUrls: IThirdPartyDocumentPresignedUrl[] = patchResponse.data || [];
        // Presigned URLs come back only for the new (fileId null) entries — upload those, in order.
        const newFiles = input.files.filter(f => f.file).map(f => ({ file: f.file! }));
        if (newFiles.length > 0 && presignedUrls.length > 0) {
          const uploadResult = await uploadFilesWithPresignedUrls(presignedUrls, newFiles, languageCode);
          // The record IS saved even if some files fail. Resolve with the outcome so the UI can
          // confirm the save, report how many files failed, and mark the uploaded ones (no re-upload on retry).
          return { resultId: createdResultId, uploadedFiles: uploadResult.successes, failedCount: uploadResult.errors.length };
        }
      }

      return { resultId: createdResultId, uploadedFiles: [], failedCount: 0 };
    } catch (error) {
      return thunkApi.rejectWithValue({
        message: prettifyErrorResponse(transformErrorResponse(error)),
        resultId: createdResultId,
      });
    }
  },
);

const updateWorkerThirdPartyDrugTestResult = createAsyncThunk(
  actionMapForOtherLoaders.updateWorkerThirdPartyDrugTestResult,
  async (query: { resultId: string; input: ICreateThirdPartyDrugTestResultInput }, thunkApi) => {
    const state = thunkApi.getState() as any;
    const languageCode = state.app?.languageCode || 'en';
    const isWorker = state.user.isWorker || false;
    const workerId = state.onboarding.id || state.user?.workerId;

    // PUT body uses PascalCase field names per API spec
    const updateBody: IUpdateThirdPartyDrugTestResultBody = {
      projectId: query.input.projectId,
      panelId: query.input.panelId,
      DrugTestingTypeId: query.input.drugTestingTypeId,
      // PUT expects slash-separated date per API spec
      testedAt: query.input.testedAt.replace(/-/g, '/'),
      ClinicName: query.input.clinicName,
      Reason: query.input.reason,
      Result: query.input.result,
    };

    try {
      await Services.updateThirdPartyDrugTestResult(query.resultId, updateBody, languageCode, isWorker, workerId);

      if (query.input.files.length > 0) {
        // Set-state PATCH: keep existing docs by fileId, mark new uploads with fileId null;
        // any existing document omitted here is deleted by the API.
        const patchResponse = await Services.patchThirdPartyDrugTestResultDocuments(
          query.resultId,
          { documents: query.input.files.map(f => ({ fileId: f.fileId, fileName: f.name })) },
          languageCode,
          isWorker,
          workerId,
        );
        const presignedUrls: IThirdPartyDocumentPresignedUrl[] = patchResponse.data || [];
        // Presigned URLs come back only for the new (fileId null) entries — upload those, in order.
        const newFiles = query.input.files.filter(f => f.file).map(f => ({ file: f.file! }));
        if (newFiles.length > 0 && presignedUrls.length > 0) {
          const uploadResult = await uploadFilesWithPresignedUrls(presignedUrls, newFiles, languageCode);
          // The record IS saved even if some files fail. Resolve with the outcome so the UI can
          // confirm the save, report how many files failed, and mark the uploaded ones (no re-upload on retry).
          return { resultId: query.resultId, uploadedFiles: uploadResult.successes, failedCount: uploadResult.errors.length };
        }
      }

      return { resultId: query.resultId, uploadedFiles: [], failedCount: 0 };
    } catch (error) {
      return thunkApi.rejectWithValue({
        message: prettifyErrorResponse(transformErrorResponse(error)),
        resultId: query.resultId,
      });
    }
  },
);

const deleteWorkerThirdPartyDrugTestResult = createAsyncThunk(
  actionMapForOtherLoaders.deleteWorkerThirdPartyDrugTestResult,
  async (query: { resultId: string }, thunkApi) => {
    const state = thunkApi.getState() as any;
    const languageCode = state.app?.languageCode || 'en';
    const isWorker = state.user.isWorker || false;
    const workerId = state.onboarding.id || state.user?.workerId;

    return Services.deleteThirdPartyDrugTestResult(query.resultId, languageCode, isWorker, workerId)
      .then(() => query.resultId)
      .catch(error => thunkApi.rejectWithValue(transformErrorResponse(error)));
  },
);

const fetchWorkerCourses = createAsyncThunk(
  actionMapForDataLoaders.fetchWorkerCourses,
  async (
    query: {
      workerId: string;
      languageCode?: string;
      pageNumber?: number;
      pageSize?: number;
      courseStatus?: 1 | 3;
      sortBy?: 'coursename' | 'completiondate' | 'expirationdate';
      sortDirection?: 'ASC' | 'DESC';
    },
    thunkApi,
  ) => {
    try {
      const {
        workerId,
        languageCode = 'en',
        pageNumber = 1,
        pageSize = 9,
        courseStatus,
        sortBy = 'coursename',
        sortDirection = 'ASC',
      } = query;

      const response = await LearningServices.getWorkerCourses(
        workerId,
        {
          pageNumber,
          pageSize,
          courseStatus,
          sortBy,
          sortDirection,
        },
        languageCode,
      );

      return {
        courseStatus,
        data: response.data?.records || [],
        currentPage: response.data?.paging?.currentPage || pageNumber,
        totalPages: response.data?.paging?.totalPages || 0,
        totalRecords: response.data?.paging?.totalRecords || 0,
        limit: response.data?.paging?.limit || pageSize,
      };
    } catch (error) {
      return thunkApi.rejectWithValue(transformErrorResponse(error));
    }
  },
);

const fetchWorkerProjects = createAsyncThunk(
  actionMapForDataLoaders.fetchWorkerProjects,
  async (
    query: {
      workerId: string;
      languageCode?: string;
      pageNumber?: number;
      pageSize?: number;
      status?: number;
      sort?: string;
    },
    thunkApi,
  ) => {
    try {
      const { workerId, languageCode = 'en', pageNumber = 1, pageSize = 12, status, sort } = query;

      // If status is provided, fetch only that category
      if (status !== undefined) {
        const response = await Services.getWorkerLiteProjects(
          workerId,
          pageNumber,
          pageSize,
          status,
          sort,
          languageCode,
        );
        return {
          status,
          data: response.data?.items || [],
          pageNumber: response.data?.pageNumber || pageNumber,
          pageSize: response.data?.pageSize || pageSize,
          totalResults: response.data?.totalResults || 0,
        };
      }

      // Otherwise, fetch all three categories with pagination metadata
      const [pendingResponse, currentResponse, pastResponse] = await Promise.all([
        Services.getWorkerLiteProjects(workerId, pageNumber, pageSize, 0, sort, languageCode), // status 0 = pending
        Services.getWorkerLiteProjects(workerId, pageNumber, pageSize, 1, sort, languageCode), // status 1 = current/active
        Services.getWorkerLiteProjects(workerId, pageNumber, pageSize, 3, sort, languageCode), // status 3 = past/closed
      ]);

      return {
        pending: pendingResponse.data?.items || [],
        current: currentResponse.data?.items || [],
        past: pastResponse.data?.items || [],
        pagination: {
          pending: {
            pageNumber: pendingResponse.data?.pageNumber || pageNumber,
            pageSize: pendingResponse.data?.pageSize || pageSize,
            totalResults: pendingResponse.data?.totalResults || 0,
          },
          current: {
            pageNumber: currentResponse.data?.pageNumber || pageNumber,
            pageSize: currentResponse.data?.pageSize || pageSize,
            totalResults: currentResponse.data?.totalResults || 0,
          },
          past: {
            pageNumber: pastResponse.data?.pageNumber || pageNumber,
            pageSize: pastResponse.data?.pageSize || pageSize,
            totalResults: pastResponse.data?.totalResults || 0,
          },
        },
      };
    } catch (error) {
      return thunkApi.rejectWithValue(transformErrorResponse(error));
    }
  },
);

const verifyPassword = async (password: string, userId?: string) => {
  // In admin session, fetch the target user's contacts (admin's own or the worker being acted on)
  // to get their primary email as the auth username. In worker session, fetch own contacts via /self.
  const allContactsResponse = userId
    ? await ContactsService.getContactsForUser(userId)
    : await ContactsService.getContacts();
  const allContacts = allContactsResponse.data || [];
  const primary = allContacts.find((c: any) => c.isPrimary);
  if (!primary) throw new Error('No primary contact found');
  try {
    const authResponse = await UsersServices.authenticate({ username: primary.contactValue, password });
    if (!authResponse?.data?.sessionTokens?.idToken) throw new Error('Wrong password');
  } catch (error: any) {
    const data = error?.response?.data;
    const message = data?.detail || data?.message || null;
    throw new Error(message || 'Something went wrong. Please try again.');
  }
};

// Returns null → use /users/self; string → use /users/{id}/contacts.
// Direct worker session (isWorker=true, no admin override) → null.
// Admin viewing own profile (isAdminSessionContext=true) → admin's own userId.
// Admin acting on a worker → onboarding.userId.
const resolveContactsUserId = (state: any, isAdminSessionContext?: boolean): string | null => {
  if (state.user?.isWorker && !isAdminSessionContext) return null;
  return isAdminSessionContext ? state.user?.userId : state.onboarding?.userId;
};

const fetchEmailContacts = createAsyncThunk(
  actionMapForOtherLoaders.fetchEmailContacts,
  async (query: { isAdminSessionContext?: boolean } | void, thunkApi) => {
    try {
      const state = thunkApi.getState() as any;
      const userId = resolveContactsUserId(state, (query as any)?.isAdminSessionContext);
      const response =
        userId == null ? await ContactsService.getContacts(0) : await ContactsService.getContactsForUser(userId, 0);
      return response.data || [];
    } catch (error) {
      return thunkApi.rejectWithValue(transformErrorResponse(error));
    }
  },
);

const fetchPhoneContacts = createAsyncThunk(
  actionMapForOtherLoaders.fetchPhoneContacts,
  async (query: { isAdminSessionContext?: boolean } | void, thunkApi) => {
    try {
      const state = thunkApi.getState() as any;
      const userId = resolveContactsUserId(state, (query as any)?.isAdminSessionContext);
      const response =
        userId == null ? await ContactsService.getContacts(1) : await ContactsService.getContactsForUser(userId, 1);
      return response.data || [];
    } catch (error) {
      return thunkApi.rejectWithValue(transformErrorResponse(error));
    }
  },
);

const addContact = createAsyncThunk(
  actionMapForOtherLoaders.addContact,
  async (
    query: { contactType: 0 | 1; contactValue: string; password: string; isAdminSessionContext?: boolean },
    thunkApi,
  ) => {
    try {
      const state = thunkApi.getState() as any;
      const userId = resolveContactsUserId(state, query.isAdminSessionContext);
      await verifyPassword(query.password, userId ?? undefined);
      if (userId == null) {
        await ContactsService.addContact({ contactType: query.contactType, contactValue: query.contactValue });
        const response = await ContactsService.getContacts(query.contactType);
        return { contactType: query.contactType, data: response.data || [] };
      } else {
        await ContactsService.addContactForUser(userId, {
          contactType: query.contactType,
          contactValue: query.contactValue,
        });
        const response = await ContactsService.getContactsForUser(userId, query.contactType);
        return { contactType: query.contactType, data: response.data || [] };
      }
    } catch (error: any) {
      return thunkApi.rejectWithValue(prettifyErrorResponse(error));
    }
  },
);

const removeContact = createAsyncThunk(
  actionMapForOtherLoaders.removeContact,
  async (
    query: { contactId: string; contactType: 0 | 1; password: string; isAdminSessionContext?: boolean },
    thunkApi,
  ) => {
    try {
      const state = thunkApi.getState() as any;
      const userId = resolveContactsUserId(state, query.isAdminSessionContext);
      await verifyPassword(query.password, userId ?? undefined);
      if (userId == null) {
        await ContactsService.deleteContact(query.contactId);
        const response = await ContactsService.getContacts(query.contactType);
        return { contactType: query.contactType, data: response.data || [] };
      } else {
        await ContactsService.deleteContactForUser(userId, query.contactId);
        const response = await ContactsService.getContactsForUser(userId, query.contactType);
        return { contactType: query.contactType, data: response.data || [] };
      }
    } catch (error: any) {
      return thunkApi.rejectWithValue(prettifyErrorResponse(error));
    }
  },
);

const makePrimaryContact = createAsyncThunk(
  actionMapForOtherLoaders.makePrimaryContact,
  async (
    query: { contactId: string; contactType: 0 | 1; password: string; isAdminSessionContext?: boolean },
    thunkApi,
  ) => {
    try {
      const state = thunkApi.getState() as any;
      const userId = resolveContactsUserId(state, query.isAdminSessionContext);
      await verifyPassword(query.password, userId ?? undefined);
      if (userId == null) {
        await ContactsService.setAsPrimary(query.contactId);
        const response = await ContactsService.getContacts(query.contactType);
        return { contactType: query.contactType, data: response.data || [] };
      } else {
        await ContactsService.setAsPrimaryForUser(userId, query.contactId);
        const response = await ContactsService.getContactsForUser(userId, query.contactType);
        return { contactType: query.contactType, data: response.data || [] };
      }
    } catch (error: any) {
      return thunkApi.rejectWithValue(prettifyErrorResponse(error));
    }
  },
);

const verifyContact = createAsyncThunk(
  actionMapForOtherLoaders.verifyContact,
  async (
    query: { contactId: string; contactType: 0 | 1; password: string; isAdminSessionContext?: boolean },
    thunkApi,
  ) => {
    try {
      const state = thunkApi.getState() as any;
      const userId = resolveContactsUserId(state, query.isAdminSessionContext);
      await verifyPassword(query.password, userId ?? undefined);
      if (userId == null) {
        await ContactsService.verifyContact(query.contactId);
        const response = await ContactsService.getContacts(query.contactType);
        return { contactType: query.contactType, data: response.data || [] };
      } else {
        await ContactsService.verifyContactForUser(userId, query.contactId);
        const response = await ContactsService.getContactsForUser(userId, query.contactType);
        return { contactType: query.contactType, data: response.data || [] };
      }
    } catch (error: any) {
      return thunkApi.rejectWithValue(prettifyErrorResponse(error));
    }
  },
);

const fetchProfileCustomFieldsConfig = createAsyncThunk(
  actionMapForOtherLoaders.fetchProfileCustomFieldsConfig,
  async (
    query: {
      workerId: string;
      companyId?: string;
      projectId?: string;
      allProjectsSetting?: boolean;
      showAcceptedProjectsSettingsOnly?: boolean;
      languageCode?: string;
    },
    thunkApi,
  ) => {
    const { languageCode, ...body } = query;
    return Services.getProfileCustomFieldsConfig(body, languageCode)
      .then(response => response.data)
      .catch(error => thunkApi.rejectWithValue(transformErrorResponse(error)));
  },
);

const fetchWorkerCompaniesRequirements = createAsyncThunk(
  actionMapForOtherLoaders.fetchWorkerCompaniesRequirements,
  async (
    query: {
      workerId: string;
      projectId: string;
      companyIds: string[];
      languageCode?: string;
    },
    thunkApi,
  ) => {
    return Services.getWorkerCompaniesRequirements(
      query.workerId,
      query.projectId,
      query.companyIds,
      query.languageCode || 'en',
    )
      .then(response => {
        console.log('fetchWorkerCompaniesRequirements response:', response.data);
        return response.data;
      })
      .catch(error => {
        return thunkApi.rejectWithValue(transformErrorResponse(error));
      });
  },
);

export {
  fetchConsentFormInformation,
  downloadConsentFormPdf,
  fetchMyCertifications,
  fetchRequiredCertifications,
  fetchWorkerDrugTestingSchedules,
  createWorkerDrugTestingSchedule,
  updateWorkerDrugTestingSchedule,
  deleteWorkerDrugTestingSchedule,
  fetchWorkerThirdPartyDrugTestResults,
  createWorkerThirdPartyDrugTestResult,
  updateWorkerThirdPartyDrugTestResult,
  deleteWorkerThirdPartyDrugTestResult,
  addMyCertification,
  updateMyCertification,
  deleteMyCertification,
  fetchWorkerCourses,
  fetchWorkerProjects,
  fetchEmailContacts,
  fetchPhoneContacts,
  addContact,
  removeContact,
  makePrimaryContact,
  verifyContact,
  fetchProfileCustomFieldsConfig,
  fetchWorkerCompaniesRequirements,
};
